import React from 'react';
import SearchPopup from './SearchPopup';
import UnifiedSearchPopup from './UnifiedSearchPopup';
import TrashSearchItem from './TrashSearchItem';
import HiddenNestsSection from './HiddenNestsSection';
import { ViewQueryBuilderModal } from '../../SavedViews';
import { TrashIcon } from '../icons';

/**
 * SidebarPopups Component
 * 
 * Renders all popup modals used by the sidebar:
 * - Unified search (campaigns + missions)
 * - Trash popup
 * - View query builder modal
 */
const SidebarPopups = ({
  // Unified search
  unifiedSearchOpen,
  closeUnifiedSearch,
  allActiveCampaigns,
  allSprints,
  docs = [],
  onSelectCampaign,
  onSelectMission,
  onSelectDoc,
  onCreateCampaign,
  
  // Trash (unified: campaigns + templates)
  trashPopupOpen,
  closeTrashPopup,
  allTrashedItems,
  trashCount,
  getTrashedItemInfo,
  onRestoreSprint,
  onPermanentlyDeleteSprint,
  onRestoreTemplate,
  onPermanentlyDeleteTemplate,
  onRestoreDoc,
  onPermanentlyDeleteDoc,
  onEmptyTrash,
  hiddenNestRows = [],
  unverifiedParentCount = 0,
  scanningHiddenNests = false,
  hiddenNestScanProgress = null,
  onFindHiddenNests,
  puttingBackHiddenNestId = null,
  onPutBackHiddenNest,
  onTrashHiddenNest,
  
  // View modal
  viewModalOpen,
  closeViewModal,
  onSaveView,
  editingView,
  
  // Shared
  popupPosition,
}) => {
  const renderTrashSearchItem = (item) => {
    const isDoc = item.itemType === 'doc';
    const isTemplate = item.itemType === 'template';
    return (
      <TrashSearchItem
        key={item.id}
        sprint={item}
        trashedItemInfo={isDoc ? null : getTrashedItemInfo(item)}
        isTemplate={isTemplate}
        isDoc={isDoc}
        onRestoreSprint={isDoc ? onRestoreDoc : isTemplate ? onRestoreTemplate : onRestoreSprint}
        onPermanentlyDeleteSprint={
          isDoc ? onPermanentlyDeleteDoc : isTemplate ? onPermanentlyDeleteTemplate : onPermanentlyDeleteSprint
        }
      />
    );
  };

  return (
    <>
      {/* Unified Search Popup (Campaigns + Missions + Documents) */}
      <UnifiedSearchPopup
        isOpen={unifiedSearchOpen}
        onClose={closeUnifiedSearch}
        campaigns={allActiveCampaigns}
        allSprints={allSprints}
        docs={docs}
        onSelectCampaign={onSelectCampaign}
        onSelectMission={onSelectMission}
        onSelectDoc={onSelectDoc}
        position={popupPosition}
      />

      {/* Trash Popup (campaigns + templates + docs) */}
      <SearchPopup
        isOpen={trashPopupOpen}
        onClose={closeTrashPopup}
        title="Trash"
        icon={<TrashIcon size={14} />}
        items={allTrashedItems}
        renderItem={renderTrashSearchItem}
        emptyMessage="Trash is empty"
        searchPlaceholder="Search campaigns, templates, and docs…"
        position={popupPosition}
        variant="trash"
        extraContent={(query) => (
          <HiddenNestsSection
            rows={hiddenNestRows}
            query={query}
            unverifiedParentCount={unverifiedParentCount}
            scanning={scanningHiddenNests}
            scanProgress={hiddenNestScanProgress}
            onScan={onFindHiddenNests}
            puttingBackId={puttingBackHiddenNestId}
            onPutBack={onPutBackHiddenNest}
            onTrash={onTrashHiddenNest}
          />
        )}
        footerContent={
          trashCount > 0 ? (
            <button
              className="search-popup__btn search-popup__btn--danger"
              onClick={() => {
                if (window.confirm(`Permanently delete all ${trashCount} items in trash? This cannot be undone.`)) {
                  onEmptyTrash?.();
                  closeTrashPopup();
                }
              }}
            >
              Empty Trash
            </button>
          ) : null
        }
      />

      {/* View Query Builder Modal */}
      <ViewQueryBuilderModal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        onSave={onSaveView}
        editingView={editingView}
      />
    </>
  );
};

export default SidebarPopups;
